import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import Layout from '../Layout';

const PrivacyPage = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();

  // Direct inline style for title color
  const titleStyle = {
    fontWeight: 700,
    color: isDark ? '#0ea5e9' : '#6b21a8' // sky-500 for dark, purple-800 for light
  };

  return (
    <Layout>
      <div className="space-y-6">
        <Helmet>
          <title>{language === 'zh' ? '隱私政策 | Beat Buddy 節拍器' : 'Privacy Policy | Beat Buddy Metronome'}</title>
          <meta name="description" content={language === 'zh' ? "了解 Beat Buddy 節拍器應用如何保護您的隱私和個人數據。我們的隱私政策詳細說明了我們收集和使用數據的方式。" : "Learn how the Beat Buddy metronome app protects your privacy and personal data. Our privacy policy details how we collect and use data."} />
          <meta name="keywords" content="Beat Buddy, privacy policy, data protection, user data, privacy rights, metronome" />
          <link rel="canonical" href="https://beatbuddy.app/privacy" />
        </Helmet>

        <div className="text-center max-w-xl mx-auto">
          <h2 className="text-3xl mb-2">
            <span style={titleStyle}>{t('privacyTitle')}</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mx-auto">
            {t('privacySubtitle')}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl">
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <h3 className="text-xl font-semibold mb-4 text-purple-600 dark:text-purple-400">{t('overview')}</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              {t('overviewText1')}
            </p>
            <p className="mb-8 text-gray-700 dark:text-gray-300">
              {t('overviewText2')}
            </p>

            <h3 className="text-xl font-semibold mb-4 text-purple-600 dark:text-purple-400">{t('infoCollected')}</h3>
            <p className="mb-2 text-gray-700 dark:text-gray-300"><strong className="text-gray-800 dark:text-gray-200">{t('autoCollectedInfo')}</strong></p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300">
              <li>{t('autoCollectedPoint1')}</li>
              <li>{t('autoCollectedPoint2')}</li>
              <li>{t('autoCollectedPoint3')}</li>
            </ul>
            <p className="mb-2 text-gray-700 dark:text-gray-300"><strong className="text-gray-800 dark:text-gray-200">{t('userProvidedInfo')}</strong></p>
            <ul className="list-disc pl-6 mb-8 text-gray-700 dark:text-gray-300">
              <li>{t('userProvidedPoint1')}</li>
              <li>{t('userProvidedPoint2')}</li>
              <li>{t('userProvidedPoint3')}</li>
            </ul>

            <h3 className="text-xl font-semibold mb-4 text-purple-600 dark:text-purple-400">{t('infoUsage')}</h3>
            <p className="mb-2 text-gray-700 dark:text-gray-300">{t('infoUsageText')}</p>
            <ul className="list-disc pl-6 mb-8 text-gray-700 dark:text-gray-300">
              <li>{t('infoUsagePoint1')}</li>
              <li>{t('infoUsagePoint2')}</li>
              <li>{t('infoUsagePoint3')}</li>
              <li>{t('infoUsagePoint4')}</li>
              <li>{t('infoUsagePoint5')}</li>
            </ul>

            <h3 className="text-xl font-semibold mb-4 text-purple-600 dark:text-purple-400">{t('dataStorage')}</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              {t('dataStorageText1')}
            </p>
            <p className="mb-8 text-gray-700 dark:text-gray-300">
              {t('dataStorageText2')}
            </p>

            <h3 className="text-xl font-semibold mb-4 text-purple-600 dark:text-purple-400">{t('dataSharing')}</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              {t('dataSharingText')}
            </p>
            <ul className="list-disc pl-6 mb-8 text-gray-700 dark:text-gray-300">
              <li>{t('dataSharingPoint1')}</li>
              <li>{t('dataSharingPoint2')}</li>
              <li>{t('dataSharingPoint3')}</li>
            </ul>

            <h3 className="text-xl font-semibold mb-4 text-purple-600 dark:text-purple-400">{t('yourChoices')}</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300">{t('yourChoicesText')}</p>
            <ul className="list-disc pl-6 mb-8 text-gray-700 dark:text-gray-300">
              <li>{t('yourChoicesPoint1')}</li>
              <li>{t('yourChoicesPoint2')}</li>
              <li>{t('yourChoicesPoint3')}</li>
              <li>{t('yourChoicesPoint4')}</li>
            </ul>

            <h3 className="text-xl font-semibold mb-4 text-purple-600 dark:text-purple-400">{t('childrenPrivacy')}</h3>
            <p className="mb-8 text-gray-700 dark:text-gray-300">
              {t('childrenPrivacyText')}
            </p>

            <h3 className="text-xl font-semibold mb-4 text-purple-600 dark:text-purple-400">{t('policyChanges')}</h3>
            <p className="mb-8 text-gray-700 dark:text-gray-300">
              {t('policyChangesText')}
            </p>

            <h3 className="text-xl font-semibold mb-4 text-purple-600 dark:text-purple-400">{t('contactUsPrivacy')}</h3>
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              {t('contactUsPrivacyText')}
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              <strong className="text-gray-800 dark:text-gray-200">{t('email')}：</strong> info@homy.hk
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPage;